import React, { useCallback, useEffect, useRef, useState } from "react";
import { Title } from "react-admin";
import { useHistory } from "react-router-dom";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@material-ui/core";
import KeyboardArrowRightIcon from "@material-ui/icons/KeyboardArrowRight";

import {
  InboundIcon,
  OutboundIcon,
} from "../../../components/common/iconography";
import ShipsTogetherBadge from "../../../components/common/shipWith/ShipsTogetherBadge";
import { getActivePortalType, logError } from "../../../utils/appUtils";
import { GateCheckInStatus, PortalType } from "../../../utils/constants";
import { formatInYardTimeZone } from "../../../utils/dateUtils";
import { groupCheckInShipmentIds } from "../../../utils/shipWith";
import {
  dedupeActiveCheckIns,
  gateStatusRefusal,
  gateStatusRestriction,
  isBoundByQueueOrder,
  nextInGateQueue,
  sortByGateServiceTime,
} from "../../../utils/gateQueueOrder";
import {
  GateDockField,
  GateSaveButton,
  GateStatusSelect,
} from "./GateCheckInFields";
import {
  GATE_ACTIVE_STATUSES as ACTIVE_STATUSES,
  fetchGateCheckInsByStatus,
  placedShipmentPath,
  shipmentEditPath,
} from "./gateCheckInUtils";
import { fetchGateShipments } from "./gatePlacement";
import { useGateCheckInEditor } from "./useGateCheckInEditor";
const REFRESH_INTERVAL = 20 * 1000; // EventsCalendar poll precedent

const { DOCKED } = GateCheckInStatus;

/** Where a row leads, which is the portal the viewer is working: the Manager
 * Portal opens the shipment's edit form, the Operator App opens the crew
 * process page. The driver API writes exactly one of the two ids.
 *
 * Null when a row names neither. Such a row is still listed and still worked —
 * dedupeActiveCheckIns keeps it because a truck at the gate is at the gate
 * whatever its row is missing, and its status and dock are still savable from
 * here — but there is no shipment to open, and a path built from the id that
 * is not there navigates to a form for "undefined". */
export const shipmentPath = (checkIn, crewPortal) => {
  const shipmentId = checkIn.outboundId || checkIn.inboundId;
  if (!shipmentId) return null;
  const isOutbound = Boolean(checkIn.outboundId);
  return crewPortal
    ? placedShipmentPath(shipmentId, isOutbound)
    : shipmentEditPath(shipmentId, isOutbound);
};

/** One queue row owns its own editor, which is the same editor the shipment
 * dispatch tabs run — the queue and the forms cannot drift apart. Advancing to
 * DOCKED places the shipment first (saveGateCheckIn, ahead of its own write),
 * so a shipment that cannot be placed keeps its driver where the queue says.
 *
 * `orderBound` says the viewer calls drivers in queue order (a supervisor); with
 * it, a waiting row that is not `isNextInQueue` cannot be advanced, and
 * `nextLabel` names the truck that goes first. A manager gets neither — but
 * both are held to the departure rule, which is about the row and its load
 * rather than about them: a visit only ends as DEPARTED from the dock, once
 * the load is worked. `shipments` are the docked row's loads (empty until
 * read, or for a row naming none) — one for a single-load truck, every linked
 * load for an LTL "ship with" truck — which is what that second half is
 * judged on: all of them have to be worked. */
const GateQueueRow = ({
  checkIn,
  shipments,
  onChanged,
  orderBound,
  isNextInQueue,
  nextLabel,
  crewPortal,
}) => {
  const editor = useGateCheckInEditor(checkIn, {
    onSaved: onChanged,
    onReload: onChanged,
  });
  const history = useHistory();
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  // Which shipment the check-in points at is the direction — the driver API
  // writes exactly one of the two ids. Same icons the crew Gate Queue puts on
  // its avatars, so both surfaces read the same at a glance.
  const isOutbound = Boolean(checkIn.outboundId);
  const DirectionIcon = isOutbound ? OutboundIcon : InboundIcon;
  const openPath = shipmentPath(checkIn, crewPortal);
  const openShipment = () => history.push(openPath);
  // The Operator App target is the crew process page, which only has work on it
  // once the load is spotted — so there, Open waits for DOCKED. The SAVED
  // status, not the one staged in the select: only a saved DOCKED has placed
  // the shipment. The Manager Portal target is the edit form, which reads
  // correctly at any gate status, so there the button is always offered — but
  // only where there is a shipment to open.
  const showOpen =
    Boolean(openPath) && (!crewPortal || checkIn.status === DOCKED);
  // Both rules the select answers to: the queue order, which binds a
  // supervisor, and the completion rule, which binds everyone — a visit only
  // ends as DEPARTED from the dock.
  const isStatusAllowed = (status) =>
    !gateStatusRefusal({
      status,
      currentStatus: checkIn.status,
      orderBound,
      isNextInQueue,
      shipments,
    });
  // Every load this row controls. Two or more is a linked truck, and the
  // badge beside the reference says so — the row opens the searched load,
  // and the office has to know there are siblings to open after it.
  const controlledIds = groupCheckInShipmentIds(checkIn);
  // What the control says about what it has greyed out: the queue holding a
  // truck back behind another still at the gate, or a visit that cannot be
  // completed from where it stands.
  const restriction = gateStatusRestriction({
    currentStatus: checkIn.status,
    orderBound,
    isNextInQueue,
    nextLabel,
  });
  // No room for an extra column on a phone, so the whole row carries the
  // navigation there. The editing cells stop the click from bubbling — changing
  // status or dock must not navigate away mid-edit.
  const rowOpens = showOpen && isSmall;
  const stopRowClick = rowOpens ? (e) => e.stopPropagation() : undefined;
  return (
    <TableRow
      hover={rowOpens}
      style={rowOpens ? { cursor: "pointer" } : undefined}
      onClick={rowOpens ? openShipment : undefined}
    >
      {/* Direction rides in an unlabelled first column: the icon carries the
          meaning, and a heading would only repeat it. */}
      <TableCell padding="checkbox">
        <Tooltip title={isOutbound ? "Outbound" : "Inbound"}>
          <DirectionIcon fontSize="small" color="action" />
        </Tooltip>
      </TableCell>
      {/* Yard wall clock, not the viewer's: a manager watching this
          queue from another time zone would otherwise read check-in
          times shifted by hours, and disagreeing with every other list
          column in the app. */}
      <TableCell>
        {checkIn.scheduledDateTime
          ? formatInYardTimeZone(checkIn.scheduledDateTime, "h:mm a")
          : "—"}
      </TableCell>
      <TableCell>{formatInYardTimeZone(checkIn.arrivedAt, "h:mm a")}</TableCell>
      <TableCell>{checkIn.driverName || "—"}</TableCell>
      <TableCell>{checkIn.carrierName || "—"}</TableCell>
      <TableCell>
        <span
          style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
        >
          {checkIn.referenceEntered}
          <ShipsTogetherBadge
            ids={controlledIds}
            selfId={checkIn.outboundId}
            compact={isSmall}
          />
        </span>
      </TableCell>
      <TableCell>{checkIn.truckPlate || "—"}</TableCell>
      <TableCell onClick={stopRowClick}>
        <GateStatusSelect
          editor={editor}
          variant="table"
          isStatusAllowed={isStatusAllowed}
          restriction={restriction}
        />
      </TableCell>
      <TableCell onClick={stopRowClick}>
        <GateDockField editor={editor} variant="table" />
      </TableCell>
      <TableCell onClick={stopRowClick}>
        <GateSaveButton editor={editor} />
      </TableCell>
      {!isSmall && (
        <TableCell padding="none">
          {showOpen && (
            <Button
              color="primary"
              size="small"
              endIcon={<KeyboardArrowRightIcon />}
              onClick={openShipment}
            >
              Open
            </Button>
          )}
        </TableCell>
      )}
    </TableRow>
  );
};

/** The loads behind the docked rows, keyed by check-in id — every load the
 * row controls, so a linked truck's row lists all of its members. Only a
 * docked row can be marked DEPARTED, and only once its load is worked (end
 * time or COMPLETED), so those are the rows whose shipments the select has to
 * know. One read per docked row, in parallel; a read that fails leaves its
 * row unjudged here (an empty list) and the write guard settles it. Exported
 * for tests. */
export const fetchDockedShipments = async (checkIns) => {
  const docked = checkIns.filter((c) => c.status === DOCKED);
  const found = await Promise.all(
    docked.map((c) =>
      fetchGateShipments(c).catch((err) => {
        logError("GateQueuePage:fetchDockedShipments", err, false);
        return [];
      }),
    ),
  );
  const byCheckIn = {};
  docked.forEach((c, index) => {
    byCheckIn[c.id] = found[index].map(({ shipment }) => shipment);
  });
  return byCheckIn;
};

/** Live gate queue (plan AD-17 / spec D2). Yard staff move drivers through
 * ARRIVED → CALLED → DOCKED → DEPARTED; the driver's phone polls the
 * same row (driverApi #9). Terminal statuses also release the shipment's
 * gateCheckInId lock so the next driver can check in (plan AD-7). */
export const GateQueuePage = () => {
  const [rows, setRows] = useState([]);
  const [shipments, setShipments] = useState({});
  const isSmall = useMediaQuery((theme) => theme.breakpoints.down("xs"));
  const yardId = window.appUser?.reloadYardId;
  // Poll, visibility-return and a child's onChanged all call fetchQueue, so
  // several reads can be in flight together. A monotonic request id (the same
  // guard the Scheduler's availabilityRequestRef uses) marks the latest read; a
  // slower earlier one that resolves after it is dropped rather than overwriting
  // the table with staler rows.
  const fetchRequestRef = useRef(0);
  // A supervisor calls the drivers in the published order; a manager, who owns
  // that schedule, calls whoever they like.
  const orderBound = isBoundByQueueOrder(window.appUser?.type);
  // Both menus route here, so the portal the viewer switched to is what decides
  // where a row's Open button lands — the yard office works the edit forms, the
  // yard floor works the process pages.
  const crewPortal = getActivePortalType() === PortalType.CREW;

  const fetchQueue = useCallback(async () => {
    if (!yardId) return;
    const requestId = fetchRequestRef.current + 1;
    fetchRequestRef.current = requestId;
    try {
      // One read per status, every page of each — this is the whole gate, and
      // a truck missing from it is a truck the office cannot move.
      const checkIns = await fetchGateCheckInsByStatus(yardId, ACTIVE_STATUSES);
      // Drop a read a newer one has already superseded, so a slow poll cannot
      // overwrite the fresher table a later fetch just set — and so a poll
      // nobody is waiting for stops before it pays for the shipment reads
      // below.
      if (requestId !== fetchRequestRef.current) return;
      // The reads arrive grouped by status, so they are re-sorted into gate
      // queue order across all of them: this page and the crew Gate Queue show
      // the same trucks from different tables, and a manager calling the next
      // driver has to see the order the yard floor sees. The appointment rides
      // on the check-in row (older rows have none and fall back to their
      // check-in time).
      //
      // Deduped first, for the same reason the crew page keys its rows by
      // shipment: a stale active row left behind by a failed write would put
      // one truck in this list twice, and the two entries would answer
      // "who is next" differently on the two pages.
      setRows(
        sortByGateServiceTime(dedupeActiveCheckIns(checkIns), (c) => [
          c.scheduledDateTime,
          c.arrivedAt,
        ]),
      );
      // The loads land after the table rather than gating it: the gate queue
      // is what the office is watching, and the shipments only decide whether
      // one menu item is greyed. Re-read every tick and not cached against the
      // check-in row, because what they are watched for — a load finishing —
      // changes the shipment while the check-in stands still.
      const dockedShipments = await fetchDockedShipments(checkIns);
      if (requestId !== fetchRequestRef.current) return;
      setShipments(dockedShipments);
    } catch (err) {
      logError("GateQueuePage:fetch", err, false);
    }
  }, [yardId]);

  useEffect(() => {
    fetchQueue();
    // Skip ticks while the tab is hidden, and catch up on return. A yard office
    // machine that leaves this tab open all day otherwise runs three AppSync
    // queries every 20 seconds for a page nobody is looking at. Same shape as
    // the driver app's useCheckInPoll.
    const intervalId = setInterval(() => {
      if (document.visibilityState !== "hidden") fetchQueue();
    }, REFRESH_INTERVAL);
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchQueue();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [fetchQueue]);

  // Whose turn it is, computed once for the table: rows are already in queue
  // order, so the first one still waiting at the gate is the truck to call.
  const next = orderBound ? nextInGateQueue(rows, (c) => c.status) : null;
  const nextLabel = next?.driverName || next?.carrierName || null;

  return (
    <Paper style={{ padding: 16, marginTop: 16 }}>
      <Title title="Gate Queue" />
      <Typography variant="h6">Gate queue</Typography>
      <Table size="small">
        <TableHead>
          <TableRow>
            {[
              "dir",
              "Appt",
              "Arrived",
              "Driver",
              "Carrier",
              "Reference",
              "Plate",
              "Status",
              "Stop",
              "save",
              // The Open column only exists on wider screens, where the button
              // has room; on a phone the row itself navigates.
              ...(isSmall ? [] : ["open"]),
            ].map((h) => (
              // "dir", "save" and "open" are React keys only — those columns
              // stay unlabelled.
              <TableCell key={h}>
                {["dir", "save", "open"].includes(h) ? "" : h}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((c) => (
            <GateQueueRow
              key={c.id}
              checkIn={c}
              shipments={shipments[c.id] || []}
              onChanged={fetchQueue}
              orderBound={orderBound}
              isNextInQueue={Boolean(next) && next.id === c.id}
              nextLabel={nextLabel}
              crewPortal={crewPortal}
            />
          ))}
          {rows.length === 0 && (
            <TableRow>
              <TableCell colSpan={isSmall ? 10 : 11}>
                <Typography color="textSecondary">
                  No drivers waiting.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Paper>
  );
};
